import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { PageTabRail } from '../../../../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../components/router/router-tabbed-navigation';
import { OrganizationBreadcrumb } from '../../../../../../../../features/userauth/organization/organization-breadcrumb';
import { UserauthRealmAccessTable } from '../../../../../../../../features/userauth/realm-access/userauth-realm-access-table';
import { getPageHead } from '../../../../../../../../util/routing/meta';
import type { PlatformWorkspaceViewContext } from '../../../../../../../../util/view-context';

export const Route = createFileRoute('/_app/_platform/_context-required/organization/_organization/$organizationId/_detail/realm-access')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.Organization,
      label: 'Realm Access',
      order: 2,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.Organization>,
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `organization-detail-${params.organizationId}-realm-access`,
        label: <OrganizationBreadcrumb organizationId={params.organizationId} />,
        to: `/organization/${params.organizationId}/realm-access`,
        suffix: (
          <CopyButton
            content={params.organizationId}
            label="Copy Organization ID"
            tooltip={{ copy: params.organizationId, copied: 'Copied Organization ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Organization'),
  component: OrganizationRealmAccess,
});

function TableHeader() {
  return (
    <PageTabRail actions={<HorizontalStack blockAlign="center" gap={2} wrap="nowrap"></HorizontalStack>} group={TabbedNavigationGroup.Organization} />
  );
}

function OrganizationRealmAccess() {
  const { organizationId } = Route.useParams();
  const viewContext = useMemo(
    () =>
      ({
        kind: 'rollup-organization',
        organizationId,
      }) as const satisfies PlatformWorkspaceViewContext,
    [organizationId],
  );

  return <UserauthRealmAccessTable HeaderActions={TableHeader} viewContext={viewContext} />;
}
